import React from "react";
import { motion } from "framer-motion";
import coderAvatar from "../../assets/images/about_coder_illustration.png";

const About = () => {
  const coreValues = [
    { 
      title: "Clean Architecture", 
      desc: "Decoupled logic, dry domain models, Salesforce Apex, and custom Shopify solutions." 
    },
    { 
      title: "Automation-First", 
      desc: "Developing custom n8n webhooks and scripts that eliminate hours of manual entry." 
    },
    { 
      title: "Scale & Performance", 
      desc: "Observability dashboards, Redis caching keys, and database aggregation indices." 
    },
    { 
      title: "Security Conscious", 
      desc: "Implementing OWASP standards, JWT credentials, and secure CRM sync sessions." 
    }
  ];

  return (
    <section
      id="about"
      className="px-4 pt-28 pb-16 md:py-24 flex justify-center items-center relative overflow-hidden"
    >
      {/* Background glowing blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: true }}
        className="w-full max-w-6xl flex flex-col-reverse md:flex-row items-center justify-between gap-12 p-6 md:p-12 rounded-3xl border border-white/10 backdrop-blur-lg bg-white/5 shadow-2xl transition-transform hover:-translate-y-1 duration-300 relative z-10"
      >
        {/* Text Content */}
        <div className="flex-2 text-white space-y-6 mt-6 md:mt-0 max-w-2xl text-left">
          <div className="flex items-center gap-3">
            <span className="h-1.5 w-10 bg-cyan-400 rounded-full" />
            <span className="text-xs uppercase tracking-[0.3em] text-cyan-400 font-bold">Background</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">
            About Me
          </h2>

          <div className="space-y-4 text-gray-300 text-sm md:text-base leading-relaxed">
            <p>
              Hi, I'm <span className="text-cyan-300 font-semibold">Chahat Jain</span>. I specialize in building full-stack applications, automated pipelines, and intelligent CRM integrations that streamline operations.
            </p>
            <p>
              With over 2 years of experience at <span className="text-indigo-300 font-semibold">TwoPir Consulting</span>, I have successfully delivered 20+ projects connecting Salesforce, HubSpot, and custom REST API endpoints to save manual business hours.
            </p>
            <p>
              I leverage the MERN stack, Salesforce PD1 standards, custom Shopify App & Theme architectures, and workflow tools like n8n/Zapier to model robust, scalable logic. I focus on clean code, solid testing coverage, and sub-second performance.
            </p>
          </div>

          {/* Core Values Mini Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
            {coreValues.map((val, idx) => (
              <div 
                key={idx} 
                className="p-4 bg-white/5 border border-white/5 rounded-2xl shadow-md hover:border-cyan-500/20 transition-all duration-300"
              >
                <h4 className="text-xs font-bold text-cyan-300 uppercase tracking-wider mb-1">{val.title}</h4>
                <p className="text-[11px] text-gray-400 leading-normal">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Image Content */}
        <div className="flex-1 flex items-center justify-center relative w-full max-w-xs md:max-w-none">
          {/* Backing neon glow */}
          <div className="absolute w-72 h-72 bg-gradient-to-tr from-cyan-500/10 to-indigo-500/10 rounded-full blur-2xl opacity-50 pointer-events-none" />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
            whileHover={{ scale: 1.02 }}
            className="relative p-2.5 bg-white/5 border border-white/10 rounded-3xl shadow-2xl backdrop-blur-md overflow-hidden max-w-[280px] md:max-w-[320px] w-full"
          >
            <img
              src={coderAvatar}
              alt="Chahat Jain"
              className="w-full h-auto object-cover rounded-2xl filter drop-shadow-[0_0_15px_rgba(6,182,212,0.15)]"
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default About;
